import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePoll } from "../components/PollContext";
import { ArrowRight, ChevronRight, Smartphone, Copy, Check, Share2, LogIn } from "lucide-react";
import { nanoid } from "nanoid";
import {
    createPoll as apiCreatePoll,
    joinPoll as apiJoinPoll,
    getPollBySlug,
    getMyPolls,
} from "../services/pocketbaseApi";
import { personalLink, rememberPoll, rememberedPolls } from "../identity";

export const inputClass =
    "h-[52px] w-full rounded-[14px] border-[1.5px] border-polley-line bg-[#FBFCFA] px-4 text-base text-polley-ink placeholder:text-[#6B7A71] focus:outline-none focus:border-polley-forest focus:ring-[3px] focus:ring-polley-forest/20";
const primaryButton =
    "h-14 w-full rounded-2xl bg-polley-forest text-white text-base font-semibold flex items-center justify-center gap-2.5 hover:bg-polley-forest-dark disabled:opacity-50 disabled:cursor-not-allowed";
const cardClass = "bg-white rounded-[28px] p-6 sm:p-8 shadow-[0_1px_0_#D8E0D9,0_24px_48px_-24px_rgba(20,57,43,.25)]";

function ErrorNote({ children }) {
    return (
        <div role="alert" className="rounded-2xl bg-[#FBEDE7] text-polley-no px-4 py-3 text-sm">
            {children}
        </div>
    );
}

function PersonalLinkPanel() {
    const link = personalLink();
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // In-app browsers sometimes block the clipboard; the link stays selectable.
        }
    };

    return (
        <div className="bg-white rounded-[22px] p-5 sm:p-6 flex flex-col gap-3">
            <h3 className="m-0 font-display font-bold text-xl">Your polls on another device</h3>
            <p className="m-0 text-[15px] leading-relaxed text-polley-muted">
                Open this link on your laptop, or inside WhatsApp or Facebook, and your polls and name come along.
                Keep it to yourself: anyone with it can vote as you.
            </p>
            <div className="flex items-center gap-2 bg-polley-soft rounded-[14px] p-1.5 pl-3.5">
                <input
                    readOnly
                    aria-label="Your personal link"
                    value={link}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 bg-transparent text-sm text-polley-ink focus:outline-none"
                />
                <button
                    type="button"
                    onClick={copy}
                    className="h-11 px-3.5 rounded-[10px] bg-polley-forest text-white text-sm font-semibold flex items-center gap-1.5"
                >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? "Copied" : "Copy"}
                </button>
                {navigator.share && (
                    <button
                        type="button"
                        aria-label="Share your personal link"
                        onClick={() => navigator.share({ title: "My Polley polls", url: link }).catch(() => {})}
                        className="h-11 w-11 flex-none rounded-[10px] border-[1.5px] border-polley-line bg-white flex items-center justify-center"
                    >
                        <Share2 size={16} />
                    </button>
                )}
            </div>
        </div>
    );
}

export default function PollEntry() {
    const [pollName, setPollName] = useState("");
    const [creatorName, setCreatorName] = useState("");
    const [joinerName, setJoinerName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [invitePoll, setInvitePoll] = useState(null);
    const [myPolls, setMyPolls] = useState([]);
    const [showLink, setShowLink] = useState(false);
    const { joinPoll } = usePoll();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const loadInvitePoll = async () => {
            const params = new URLSearchParams(location.search);
            const slug = params.get("poll");
            if (slug) {
                const storedPoll = localStorage.getItem(`poll_${slug}`);
                if (storedPoll) {
                    navigate(`/poll/${slug}`);
                    return;
                }

                setLoading(true);
                try {
                    const pollData = await getPollBySlug(slug);
                    setInvitePoll({ ...pollData, slug });
                } catch (err) {
                    if (err.message && err.message.includes("autocancelled")) return;
                    setError(`Poll not found: ${err.message}`);
                } finally {
                    setLoading(false);
                }
            } else {
                setInvitePoll(null);
            }
        };
        loadInvitePoll();
    }, [location.search, navigate]);

    useEffect(() => {
        const local = rememberedPolls();
        setMyPolls(local);
        getMyPolls(local.map(({ slug, participantName }) => ({ slug, name: participantName })))
            .then((linked) => {
                // Polls that arrive through the personal link get remembered here,
                // so opening them skips the name prompt.
                linked.forEach((p) => {
                    if (!localStorage.getItem(`poll_${p.slug}`)) rememberPoll(p.slug, p.participantName, p.name);
                });
                const linkedSlugs = new Set(linked.map((p) => p.slug));
                setMyPolls([...linked, ...local.filter((p) => !linkedSlugs.has(p.slug))]);
            })
            .catch(() => {});
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const slug = nanoid(10);
            const poll = await apiCreatePoll({
                name: pollName,
                creator_name: creatorName,
                slug,
            });
            joinPoll(poll, creatorName);
            navigate(`/poll/${poll.slug}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleJoinInvite = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const poll = await apiJoinPoll(invitePoll.slug, { name: joinerName });
            joinPoll(poll, joinerName);
            navigate(`/poll/${poll.slug}`);
        } catch (err) {
            if (err.message.includes("already taken")) {
                setError("That name is already in this poll. If it's you, tap it above.");
            } else {
                setError(`Failed to join poll: ${err.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const continueAs = async (name) => {
        joinPoll(invitePoll, name);
        await getMyPolls([{ slug: invitePoll.slug, name }]).catch(() => {});
        navigate(`/poll/${invitePoll.slug}`);
    };

    if (invitePoll) {
        const participants = invitePoll.participants || [];
        return (
            <div className="px-4 sm:px-6 pt-4 pb-20">
                <div className={`${cardClass} max-w-[560px] mx-auto flex flex-col gap-6`}>
                    <div className="flex flex-col gap-2">
                        <span className="self-start bg-polley-mint text-polley-forest font-semibold text-[13px] px-3 py-1.5 rounded-full">
                            You're invited
                        </span>
                        <h1 className="m-0 font-display font-extrabold text-4xl sm:text-5xl leading-none tracking-tight break-words">
                            {invitePoll.name}
                        </h1>
                        <p className="m-0 text-polley-muted">
                            Asked by <strong className="text-polley-ink">{invitePoll.creator_name}</strong>, {participants.length}{" "}
                            {participants.length === 1 ? "participant" : "participants"}
                        </p>
                    </div>

                    {participants.length > 0 && (
                        <section aria-labelledby="already" className="flex flex-col gap-3">
                            <h2 id="already" className="m-0 text-sm font-semibold">Already in this poll? Tap your name</h2>
                            <ul className="list-none m-0 p-0 flex flex-wrap gap-2">
                                {participants.map((p) => (
                                    <li key={p.id}>
                                        <button
                                            type="button"
                                            onClick={() => continueAs(p.name)}
                                            className="min-h-[44px] pl-2 pr-4 rounded-full bg-polley-sage text-polley-forest font-semibold flex items-center gap-2 hover:bg-polley-mint"
                                        >
                                            <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-sm font-bold">
                                                {p.name.charAt(0).toUpperCase()}
                                            </span>
                                            {p.name}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <form onSubmit={handleJoinInvite} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <label htmlFor="joiner" className="text-sm font-semibold">
                                {participants.length > 0 ? "New here? Your name" : "Your name"}
                            </label>
                            <input
                                id="joiner"
                                type="text"
                                className={inputClass}
                                placeholder="So people know who voted"
                                value={joinerName}
                                onChange={(e) => setJoinerName(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" disabled={loading || !joinerName} className={primaryButton}>
                            <LogIn size={18} className="text-polley-mint" />
                            {loading ? "Joining…" : "Join poll"}
                        </button>
                    </form>

                    {error && <ErrorNote>{error}</ErrorNote>}
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 pt-4 sm:pt-8 pb-20">
            <div className="max-w-[1120px] mx-auto flex flex-col gap-14">
                <section className="flex flex-wrap gap-12 items-center">
                    <div className="flex-[1_1_420px] min-w-0 flex flex-col gap-5">
                        <span className="self-start bg-polley-mint text-polley-forest font-semibold text-[13px] px-3 py-1.5 rounded-full">
                            Whistling decisions together
                        </span>
                        <h1 className="m-0 font-display font-extrabold text-5xl sm:text-[72px] leading-[0.98] tracking-[-0.035em]">
                            Pick a date.
                            <br />
                            Pick a place.
                            <br />
                            Done.
                        </h1>
                        <p className="m-0 text-lg sm:text-[19px] leading-relaxed text-polley-muted max-w-[460px]">
                            Ask a question, share one link and let everyone vote yes, no or maybe. Polley shows where the
                            group already agrees.
                        </p>
                    </div>

                    <form onSubmit={handleCreate} className={`${cardClass} flex-[1_1_400px] min-w-0 flex flex-col gap-5`}>
                        <h2 className="m-0 font-display font-bold text-[28px] tracking-tight">Start a poll</h2>
                        <div className="flex flex-col gap-2">
                            <label htmlFor="q" className="text-sm font-semibold">Question</label>
                            <input
                                id="q"
                                type="text"
                                className={inputClass}
                                placeholder="When do we play boardgames?"
                                value={pollName}
                                onChange={(e) => setPollName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            <label htmlFor="n" className="text-sm font-semibold">Your name</label>
                            <input
                                id="n"
                                type="text"
                                className={inputClass}
                                placeholder="So people know who asked"
                                value={creatorName}
                                onChange={(e) => setCreatorName(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" disabled={loading || !pollName || !creatorName} className={primaryButton}>
                            {loading ? "Creating…" : "Create poll"}
                            <ArrowRight size={18} className="text-polley-mint" />
                        </button>
                        {error && <ErrorNote>{error}</ErrorNote>}
                    </form>
                </section>

                <section aria-labelledby="mine" className="flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <h2 id="mine" className="m-0 font-display font-bold text-[28px] tracking-tight">My polls</h2>
                        <button
                            type="button"
                            onClick={() => setShowLink((v) => !v)}
                            aria-expanded={showLink}
                            className="min-h-[44px] px-4 rounded-full border-[1.5px] border-polley-line bg-white text-[15px] font-semibold flex items-center gap-2"
                        >
                            <Smartphone size={18} />
                            Use on another device
                        </button>
                    </div>
                    {showLink && <PersonalLinkPanel />}
                    {myPolls.length > 0 ? (
                        <ul className="list-none m-0 p-0 grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3">
                            {myPolls.map((poll) => (
                                <li key={poll.slug}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/poll/${poll.slug}`)}
                                        className="w-full flex items-center gap-3.5 px-[18px] py-4 bg-white rounded-[18px] text-left text-polley-ink hover:bg-polley-soft"
                                    >
                                        <span className="w-10 h-10 flex-none rounded-xl bg-polley-sage text-polley-forest flex items-center justify-center font-display font-extrabold text-[17px]">
                                            {poll.name.charAt(0).toUpperCase()}
                                        </span>
                                        <span className="flex-1 min-w-0 font-semibold truncate">{poll.name}</span>
                                        <ChevronRight size={18} className="text-polley-muted flex-none" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="m-0 text-polley-muted">
                            No polls yet. Start one above, or open your personal link from the device where you joined.
                        </p>
                    )}
                </section>

                <section aria-labelledby="flow" className="flex flex-col gap-4">
                    <h2 id="flow" className="m-0 font-display font-bold text-[28px] tracking-tight">Three steps to a decision</h2>
                    <ol className="list-none m-0 p-0 grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">
                        {[
                            ["01", "Ask", "Type the question and add the first options. Anyone can add more later."],
                            ["02", "Share", "Send the link in the group chat. Votes come in live, no account needed."],
                        ].map(([n, title, text]) => (
                            <li key={n} className="bg-white rounded-[22px] p-6 flex flex-col gap-2.5">
                                <span className="font-display font-extrabold text-[40px] leading-none text-polley-forest">{n}</span>
                                <strong className="text-[17px]">{title}</strong>
                                <span className="text-[15px] leading-normal text-polley-muted">{text}</span>
                            </li>
                        ))}
                        <li className="bg-polley-forest text-white rounded-[22px] p-6 flex flex-col gap-2.5">
                            <span className="font-display font-extrabold text-[40px] leading-none text-polley-mint">03</span>
                            <strong className="text-[17px]">Agree</strong>
                            <span className="text-[15px] leading-normal text-polley-on-forest">
                                Options everyone said yes to float to the top. That is your answer.
                            </span>
                        </li>
                    </ol>
                </section>
            </div>
        </div>
    );
}
